import { attributes } from '../../core/ids-attributes';
import { IdsBaseConstructor } from '../../core/ids-element';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

/**
 * A mixin that adds autoselect functionality to input components
 * @param {IdsBaseConstructor} superclass Accepts a superclass and creates a new subclass from it
 * @returns {any} The extended object
 */
const IdsAutoSelectMixin = <T extends IdsBaseConstructor>(superclass: T) => class extends superclass {
  constructor(...args: any[]) {
    super(...args);
  }

  static get attributes() {
    return [
      ...(superclass as any).attributes,
      attributes.AUTOSELECT
    ];
  }

  /**
   * Lifecycle callback when element is connected to the DOM
   */
  connectedCallback() {
    super.connectedCallback?.();
  }

  /**
   * Lifecycle callback when element is disconnected from the DOM
   * Cleans up autoselect event listeners
   */
  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#cleanupAutoselectEvents();
  }

  /**
   * When set the input will select all text on focus
   */
  set autoselect(value: boolean | string) {
    const val = stringToBool(value);
    const input = (this as any)?.input;

    if (val) {
      this.setAttribute(attributes.AUTOSELECT, '');
      this.#setupAutoselectBehavior(input);
    } else {
      this.removeAttribute(attributes.AUTOSELECT);
      this.#cleanupAutoselectEvents(input);
    }
  }

  /**
   * Get the autoselect state
   * @returns {boolean} true if autoselect is enabled
   */
  get autoselect(): boolean {
    return stringToBool(this.getAttribute(attributes.AUTOSELECT));
  }

  /**
   * Sets up autoselect behavior based on input type
   * @param {HTMLElement} input The input element
   * @private
   */
  #setupAutoselectBehavior(input: HTMLElement) {
    if (!input) return;
    if (input.nodeName === 'IDS-TRIGGER-FIELD') {
      input.setAttribute('autoselect', '');
    } else if (input.nodeName === 'INPUT' || input.nodeName === 'TEXTAREA') {
      (this as any)?.onEvent('focus', input, this.#handleAutoselect.bind(this));
    }
  }

  /**
   * Handles the autoselect focus event
   * @private
   */
  #handleAutoselect() {
    const input = (this as any)?.input;
    if (this.autoselect && (input?.nodeName === 'INPUT' || input?.nodeName === 'TEXTAREA')) {
      requestAnimationFrame(() => { // Safari needs a delay
        const activeEl = (this as unknown as HTMLElement).shadowRoot?.activeElement
          ?? document.activeElement;
        const stillFocused = activeEl === input
          || (this as unknown as HTMLElement).contains(activeEl as Node);
        if (stillFocused) {
          input?.select();
        }
      });
    }
  }

  /**
   * Cleans up autoselect event listeners
   * @param {HTMLElement} input The input element
   * @private
   */
  #cleanupAutoselectEvents(input?: HTMLElement) {
    const targetInput = input || (this as any)?.input;
    if (!targetInput) return;

    if (targetInput.nodeName === 'IDS-TRIGGER-FIELD') {
      targetInput.removeAttribute('autoselect');
    } else if (targetInput.nodeName === 'INPUT') {
      const handler = (this as any)?.handledEvents?.get('focus');
      if (handler && handler.target === targetInput) {
        (this as any)?.offEvent('focus', targetInput);
      }
    }
  }
};

export default IdsAutoSelectMixin;
